import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'

import { cn } from '@/lib/utils'

type Status = 'idle' | 'loading' | 'success' | 'error'

interface NewsletterProps {
    className?: string
}

export const Newsletter: FC<NewsletterProps> = ({ className }): ReactNode => {
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setStatus('loading')
        setMessage('')

        try {
            const response = await fetch('/api/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            })

            const data = await response.json()

            if (!response.ok) throw new Error(data?.error ?? 'Something went wrong. Please try again.')

            setStatus('success')
            setMessage(data?.message ?? 'You are subscribed. Thank you for joining.')
            setEmail('')
        } catch (error) {
            setStatus('error')
            setMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
        }
    }

    return (
        <section
            className={cn(
                'rounded-2xl border border-primary/20 bg-primary px-6 py-10 text-primary-foreground sm:px-10',
                className
            )}
        >
            <div className='mx-auto max-w-2xl text-center'>
                <h2 className='font-heading text-2xl font-bold tracking-tight sm:text-3xl'>
                    Clearer health, in your inbox
                </h2>

                <p className='mx-auto mt-3 max-w-xl text-primary-foreground/80'>
                    One practical, evidence-checked email a week. No spam, no hype, and you can unsubscribe anytime.
                </p>

                <form onSubmit={onSubmit} className='mx-auto mt-6 flex max-w-md flex-col gap-3 sm:flex-row'>
                    <input
                        type='email'
                        name='email'
                        required
                        value={email}
                        onChange={event => setEmail(event.target.value)}
                        placeholder='you@example.com'
                        aria-label='Email address'
                        className='h-11 flex-1 rounded-lg border border-transparent bg-background px-4 text-foreground outline-none focus:ring-2 focus:ring-background/70'
                    />

                    <button
                        type='submit'
                        disabled={status === 'loading'}
                        className='h-11 shrink-0 rounded-lg bg-background px-6 font-semibold text-primary transition-colors hover:bg-background/90 disabled:cursor-not-allowed disabled:opacity-70'
                    >
                        {status === 'loading' ? 'Joining...' : 'Subscribe'}
                    </button>
                </form>

                {status === 'success' && (
                    <p className='mt-4 text-sm font-medium text-primary-foreground' role='status'>
                        {message}
                    </p>
                )}

                {status === 'error' && (
                    <p className='mt-4 text-sm font-medium text-primary-foreground/90' role='alert'>
                        {message}
                    </p>
                )}
            </div>
        </section>
    )
}
